// The palette is a modal overlay, not an anchored popup. It renders on Base
// UI's Dialog (the design system's target library — RobotoButton and Popover
// already sit on Base UI) styled entirely with theme tokens, rather than MUI
// Dialog whose paper carries its own theme background.
import { Dialog } from "@base-ui/react/dialog";
import classNames from "classnames";
import * as React from "react";

import { CornerDownLeft, Search, X } from "@/shared/foundation/icons";

import type { Filter } from "../Filters";

import {
  assertStatelessPatterns,
  resolveFallback,
  type QuickSearchProperty,
} from "./propertyConfig";
import styles from "./QuickSearch.module.css";

export interface QuickSearchProps {
  open: boolean;
  onClose: () => void;
  properties: QuickSearchProperty[];
  /** Called with the composed filter when the user applies the search. */
  onSubmit: (filter: Filter) => void;
  /** Input placeholder (doubles as its aria-label). */
  placeholder?: string;
}

/**
 * The quick-find palette: a single text input that routes what the user types
 * to one field and applies it as a structured filter. There is no property
 * menu and no second step — a value matching a property's `pattern` (an id
 * shape) targets that property; everything else targets the `fallback`. Since
 * ids are prefix-shaped, a value is unambiguously one or the other, so "search
 * id and name together" needs no cross-field OR: the routing disambiguates.
 *
 * It exists alongside — not instead of — the Filter menu, which owns
 * categorical, unbounded conditions (tags, status). Quick find is for getting
 * to one known record fast.
 */
export function QuickSearch({
  open,
  onClose,
  properties,
  onSubmit,
  placeholder,
}: QuickSearchProps) {
  // Static-config assertions. Both run on EVERY render of this wrapper by
  // design — see each function's contract. They belong here and not in the
  // palette below, which mounts only once the dialog opens: a misconfigured
  // `properties` is only fail-fast if it throws before any input can reach
  // the palette at all.
  assertStatelessPatterns(properties);
  const fallbackProperty = resolveFallback(properties);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <Dialog.Portal>
        <SearchPalette
          onClose={onClose}
          properties={properties}
          fallbackProperty={fallbackProperty}
          onSubmit={onSubmit}
          placeholder={placeholder}
        />
      </Dialog.Portal>
    </Dialog.Root>
  );
}

interface SearchPaletteProps extends Omit<QuickSearchProps, "open"> {
  /**
   * Resolved by the wrapper, so {@link resolveFallback}'s cardinality
   * assertion still runs while the palette is closed.
   */
  fallbackProperty: QuickSearchProperty | undefined;
}

/**
 * The palette body. Mounted only while the dialog is open (the portal
 * unmounts it on close), so its initial state doubles as the per-open reset —
 * no reset effect needed.
 */
function SearchPalette({
  onClose,
  properties,
  fallbackProperty,
  onSubmit,
  placeholder,
}: SearchPaletteProps) {
  const [query, setQuery] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);

  const trimmed = query.trim();

  // The property the typed value routes to: the first whose `pattern`
  // recognizes it, else the fallback. `null` when nothing is typed or no
  // fallback is configured for unrecognized text.
  const target: QuickSearchProperty | null =
    trimmed.length === 0
      ? null
      : (properties.find((p) => p.pattern?.test(trimmed)) ??
        fallbackProperty ??
        null);

  const apply = () => {
    if (!target) {
      return;
    }
    onSubmit({
      type: "string",
      field: target.field,
      comparator: target.comparator ?? "CONTAINS",
      values: [trimmed],
    });
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      apply();
    }
  };

  const inputLabel = placeholder ?? "Search…";

  return (
    <>
      <Dialog.Backdrop className={styles.backdrop} />
      <Dialog.Popup
        className={styles.popup}
        aria-label="Quick search"
        initialFocus={inputRef}
      >
        <div className={styles.searchRow}>
          <Search size={16} className={styles.searchIcon} aria-hidden="true" />
          <input
            ref={inputRef}
            autoComplete="off"
            className={styles.searchInput}
            placeholder={inputLabel}
            aria-label={inputLabel}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            type="button"
            className={styles.iconButton}
            aria-label="Close quick search"
            onClick={onClose}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        {target && (
          <ul className={styles.list} role="listbox" aria-label="Quick result">
            <li role="presentation">
              <button
                type="button"
                role="option"
                aria-selected
                className={classNames(styles.row, styles.rowSelected)}
                // Keep focus in the input so typing continues uninterrupted.
                onMouseDown={(event) => event.preventDefault()}
                onClick={apply}
              >
                <span className={styles.rowName}>
                  {`${target.name} contains "${trimmed}"`}
                </span>
                {/* The apply affordance sits on the row's right edge, where a
                    check mark would in a multi-row list. */}
                <span className={styles.applyHint}>
                  <span className={styles.applyKey}>
                    <CornerDownLeft size={12} aria-hidden="true" />
                  </span>
                  apply
                </span>
              </button>
            </li>
          </ul>
        )}
      </Dialog.Popup>
    </>
  );
}
